/** @sfdc-extension-file SNK */
'use client';

import { Suspense } from 'react';
import { Await } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import { cn } from '@/lib/utils';
import {
    BRANDS_MEGAMENU_COLUMN_4_REGION_ID,
    CUSTOM_MEGA_MENU_COLUMN_REGION_IDS,
    INSIDE_SPACE_MEGAMENU_COLUMN_2_REGION_ID,
    INSIDE_SPACE_MEGAMENU_COLUMN_3_REGION_ID,
    INSIDE_SPACE_MEGAMENU_COLUMN_4_REGION_ID,
    INSIDE_SPACE_MEGAMENU_PATH,
    NEW_IN_CATEGORY_ID,
    NEW_IN_MEGAMENU_COLUMN_2_REGION_ID,
    NEW_IN_MEGAMENU_COLUMN_3_REGION_ID,
    NEW_IN_MEGAMENU_COLUMN_4_REGION_ID,
    OFFERS_MEGAMENU_COLUMN_1_REGION_ID,
    OFFERS_MEGAMENU_COLUMN_2_REGION_ID,
    OFFERS_MEGAMENU_COLUMN_3_REGION_ID,
    TRENDING_MEGAMENU_COLUMN_3_REGION_ID,
    TRENDING_MEGAMENU_COLUMN_4_REGION_ID,
    megaMenuRegionHasContent,
    type MegaMenuCustomNavSlot,
} from '@/extensions/snk/lib/mega-menu-regions';
import { getMenuVisibleChildCategories } from '@/extensions/snk/lib/utils';
import {
    INSIDE_SPACE_COLUMN_TITLE,
    TRENDING_NOW_COLUMN_TITLE,
    type MegaMenuTrendingContent,
} from '@/extensions/snk/lib/mega-menu-trending';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import { Button } from '@/shared-ui/atoms/button';
import { Link } from '@/shared-ui/atoms/link';
import { Icon } from '@/shared-ui/atoms/icon';
import { InsideSpaceIcon } from '@/shared-ui/icons/inside-space';
import { Typography } from '@/shared-ui/atoms/typography';
import { MEGA_MENU_START_COLUMN_BLEED_CLASS } from '../../../lib/mega-menu-layout';
import { getMegaMenuCuratedBrandCategories } from '../../../lib/mega-menu-brands';
import { MegaMenuCategoryLinkColumn, MegaMenuLinkColumn } from '../../molecules/mega-menu-category-link-column';
import {
    MegaMenuDropdownLayout,
    MegaMenuDropdownSkeleton,
    MegaMenuFeatureColumnSkeleton,
    type MegaMenuDropdownColumn,
    type MegaMenuSkeletonColumnType,
} from '../../molecules/mega-menu-dropdown-layout';

const BRANDS_SKELETON_COLUMNS: MegaMenuSkeletonColumnType[] = ['links', 'links', 'links', 'feature'];
const BRANDS_ERROR_SKELETON_COLUMNS: MegaMenuSkeletonColumnType[] = ['links', 'links', 'links'];
const NEW_IN_SKELETON_COLUMNS: MegaMenuSkeletonColumnType[] = ['links', 'feature', 'feature', 'feature'];
const OFFERS_SKELETON_COLUMNS: MegaMenuSkeletonColumnType[] = ['feature', 'feature', 'feature'];
const FOUR_FEATURE_SKELETON_COLUMNS: MegaMenuSkeletonColumnType[] = ['feature', 'feature', 'feature', 'feature'];
const TRENDING_SKELETON_COLUMNS: MegaMenuSkeletonColumnType[] = ['links', 'links', 'feature', 'feature'];

type MegaMenuDropdownPanelProps = {
    embeddedComponent?: ComponentWithComponentData | Promise<ComponentWithComponentData | null>;
    isLoading?: boolean;
    onNavigate?: () => void;
};

type MegaMenuDropdownPanelWithCategoriesProps = MegaMenuDropdownPanelProps & {
    brandsCategoryPromise?: Promise<ShopperProducts.schemas['Category']>;
    newInCategoryPromise?: Promise<ShopperProducts.schemas['Category']>;
    trendingContentPromise?: Promise<MegaMenuTrendingContent>;
};

function renderEmbeddedColumn(
    resolved: ComponentWithComponentData | null,
    regionId: string,
    key: string
): MegaMenuDropdownColumn | null {
    if (!megaMenuRegionHasContent(resolved, regionId)) {
        return null;
    }

    return {
        key,
        content: (
            <EmbeddedComponentRegion
                component={resolved}
                regionId={regionId}
                fallbackElement={<MegaMenuFeatureColumnSkeleton />}
                errorElement={null}
            />
        ),
    };
}

function buildEmbeddedColumns(
    resolved: ComponentWithComponentData | null,
    regionIds: string[]
): MegaMenuDropdownColumn[] {
    return regionIds
        .map((regionId) => renderEmbeddedColumn(resolved, regionId, regionId))
        .filter((column): column is MegaMenuDropdownColumn => column !== null);
}

function InsideSpaceStaticColumn({ onNavigate }: { onNavigate?: () => void }) {
    return (
        <div className="relative flex h-full flex-col items-start justify-center gap-6 p-6 text-left text-foreground">
            <div aria-hidden className={cn(MEGA_MENU_START_COLUMN_BLEED_CLASS, 'bg-snk-cream')} />
            <div className="relative space-y-4">
                <div role="img" aria-label="Inside Space" className="w-[164px] md:w-[204px]">
                    <Icon icon={InsideSpaceIcon} className="h-12 w-full shrink-0 sm:h-14" aria-hidden />
                </div>
                <Typography as="p" variant="p" weight="light">
                    Giving voice to our expertly curated product edit, Inside Space features a broad range of topical
                    editorials, proactive advice and shoppable stories.
                </Typography>
            </div>
            <Button asChild variant="tertiary" className="relative px-0">
                <Link to={INSIDE_SPACE_MEGAMENU_PATH} onClick={onNavigate}>
                    Discover Now
                </Link>
            </Button>
        </div>
    );
}

function ResolvedBrandsPanel({
    brandsCategory,
    embeddedComponent,
    onNavigate,
}: MegaMenuDropdownPanelProps & { brandsCategory: ShopperProducts.schemas['Category'] }) {
    const resolveEmbedded = (resolved: ComponentWithComponentData | null) => {
        const columns: MegaMenuDropdownColumn[] = [
            {
                key: 'affordable',
                content: (
                    <MegaMenuCategoryLinkColumn
                        title="Affordable brands"
                        categories={getMegaMenuCuratedBrandCategories(brandsCategory, 'affordable')}
                        onNavigate={onNavigate}
                    />
                ),
            },
            {
                key: 'bestselling',
                content: (
                    <MegaMenuCategoryLinkColumn
                        title="Best-selling brands"
                        categories={getMegaMenuCuratedBrandCategories(brandsCategory, 'bestselling')}
                        onNavigate={onNavigate}
                    />
                ),
            },
            {
                key: 'new',
                content: (
                    <MegaMenuCategoryLinkColumn
                        title="New to Space NK"
                        categories={getMegaMenuCuratedBrandCategories(brandsCategory, 'new')}
                        onNavigate={onNavigate}
                    />
                ),
            },
        ];

        const embeddedColumn = renderEmbeddedColumn(resolved, BRANDS_MEGAMENU_COLUMN_4_REGION_ID, 'brands-column-4');
        if (embeddedColumn) {
            columns.push(embeddedColumn);
        }

        return <MegaMenuDropdownLayout columns={columns} />;
    };

    if (embeddedComponent instanceof Promise) {
        return (
            <Suspense fallback={<MegaMenuDropdownSkeleton columns={BRANDS_SKELETON_COLUMNS} />}>
                <Await resolve={embeddedComponent} errorElement={resolveEmbedded(null)}>
                    {resolveEmbedded}
                </Await>
            </Suspense>
        );
    }

    return resolveEmbedded(embeddedComponent ?? null);
}

export function BrandsMegaMenuPanel({
    brandsCategoryPromise,
    embeddedComponent,
    isLoading,
    onNavigate,
}: MegaMenuDropdownPanelWithCategoriesProps) {
    if (isLoading || !brandsCategoryPromise) {
        return <MegaMenuDropdownSkeleton columns={BRANDS_SKELETON_COLUMNS} />;
    }

    return (
        <Suspense fallback={<MegaMenuDropdownSkeleton columns={BRANDS_SKELETON_COLUMNS} />}>
            <Await
                resolve={brandsCategoryPromise}
                errorElement={<MegaMenuDropdownSkeleton columns={BRANDS_ERROR_SKELETON_COLUMNS} />}>
                {(brandsCategory) => (
                    <ResolvedBrandsPanel
                        brandsCategory={brandsCategory}
                        embeddedComponent={embeddedComponent}
                        onNavigate={onNavigate}
                    />
                )}
            </Await>
        </Suspense>
    );
}

function ResolvedNewInPanel({
    newInCategory,
    embeddedComponent,
    onNavigate,
}: MegaMenuDropdownPanelProps & { newInCategory: ShopperProducts.schemas['Category'] }) {
    const resolveEmbedded = (resolved: ComponentWithComponentData | null) => {
        const columns: MegaMenuDropdownColumn[] = [
            {
                key: 'subcategories',
                content: (
                    <MegaMenuCategoryLinkColumn
                        title={newInCategory.name ?? 'New In'}
                        categories={getMenuVisibleChildCategories(newInCategory)}
                        shopAll={{ label: 'Shop All New', categoryId: NEW_IN_CATEGORY_ID }}
                        onNavigate={onNavigate}
                    />
                ),
            },
            ...buildEmbeddedColumns(resolved, [
                NEW_IN_MEGAMENU_COLUMN_2_REGION_ID,
                NEW_IN_MEGAMENU_COLUMN_3_REGION_ID,
                NEW_IN_MEGAMENU_COLUMN_4_REGION_ID,
            ]),
        ];

        return <MegaMenuDropdownLayout columns={columns} />;
    };

    if (embeddedComponent instanceof Promise) {
        return (
            <Suspense fallback={<MegaMenuDropdownSkeleton columns={NEW_IN_SKELETON_COLUMNS} />}>
                <Await resolve={embeddedComponent} errorElement={resolveEmbedded(null)}>
                    {resolveEmbedded}
                </Await>
            </Suspense>
        );
    }

    return resolveEmbedded(embeddedComponent ?? null);
}

export function NewInMegaMenuPanel({
    newInCategoryPromise,
    embeddedComponent,
    isLoading,
    onNavigate,
}: MegaMenuDropdownPanelWithCategoriesProps) {
    if (isLoading || !newInCategoryPromise) {
        return <MegaMenuDropdownSkeleton columns={NEW_IN_SKELETON_COLUMNS} />;
    }

    return (
        <Suspense fallback={<MegaMenuDropdownSkeleton columns={NEW_IN_SKELETON_COLUMNS} />}>
            <Await resolve={newInCategoryPromise} errorElement={<MegaMenuDropdownSkeleton columns={['links']} />}>
                {(newInCategory) => (
                    <ResolvedNewInPanel
                        newInCategory={newInCategory}
                        embeddedComponent={embeddedComponent}
                        onNavigate={onNavigate}
                    />
                )}
            </Await>
        </Suspense>
    );
}

function ResolvedEmbeddedOnlyPanel({
    embeddedComponent,
    isLoading,
    regionIds,
    skeletonColumns,
}: {
    embeddedComponent?: ComponentWithComponentData | Promise<ComponentWithComponentData | null>;
    isLoading?: boolean;
    regionIds: string[];
    skeletonColumns: MegaMenuSkeletonColumnType[];
}) {
    const resolveEmbedded = (resolved: ComponentWithComponentData | null) => {
        const columns = buildEmbeddedColumns(resolved, regionIds);
        if (columns.length === 0) {
            return null;
        }
        return <MegaMenuDropdownLayout columns={columns} />;
    };

    if (isLoading) {
        return <MegaMenuDropdownSkeleton columns={skeletonColumns} />;
    }

    if (embeddedComponent instanceof Promise) {
        return (
            <Suspense fallback={<MegaMenuDropdownSkeleton columns={skeletonColumns} />}>
                <Await resolve={embeddedComponent} errorElement={null}>
                    {resolveEmbedded}
                </Await>
            </Suspense>
        );
    }

    return resolveEmbedded(embeddedComponent ?? null);
}

export function OffersMegaMenuPanel({ embeddedComponent, isLoading }: MegaMenuDropdownPanelProps) {
    return (
        <ResolvedEmbeddedOnlyPanel
            embeddedComponent={embeddedComponent}
            isLoading={isLoading}
            regionIds={[
                OFFERS_MEGAMENU_COLUMN_1_REGION_ID,
                OFFERS_MEGAMENU_COLUMN_2_REGION_ID,
                OFFERS_MEGAMENU_COLUMN_3_REGION_ID,
            ]}
            skeletonColumns={OFFERS_SKELETON_COLUMNS}
        />
    );
}

export function InsideSpaceMegaMenuPanel({ embeddedComponent, isLoading, onNavigate }: MegaMenuDropdownPanelProps) {
    if (isLoading) {
        return <MegaMenuDropdownSkeleton columns={FOUR_FEATURE_SKELETON_COLUMNS} />;
    }

    const resolveEmbedded = (resolved: ComponentWithComponentData | null) => {
        const columns: MegaMenuDropdownColumn[] = [
            {
                key: 'inside-space-static',
                bleedStart: true,
                content: <InsideSpaceStaticColumn onNavigate={onNavigate} />,
            },
            ...buildEmbeddedColumns(resolved, [
                INSIDE_SPACE_MEGAMENU_COLUMN_2_REGION_ID,
                INSIDE_SPACE_MEGAMENU_COLUMN_3_REGION_ID,
                INSIDE_SPACE_MEGAMENU_COLUMN_4_REGION_ID,
            ]),
        ];

        return <MegaMenuDropdownLayout columns={columns} />;
    };

    if (embeddedComponent instanceof Promise) {
        return (
            <Suspense fallback={<MegaMenuDropdownSkeleton columns={FOUR_FEATURE_SKELETON_COLUMNS} />}>
                <Await resolve={embeddedComponent} errorElement={resolveEmbedded(null)}>
                    {resolveEmbedded}
                </Await>
            </Suspense>
        );
    }

    return resolveEmbedded(embeddedComponent ?? null);
}

export function TrendingMegaMenuPanel({
    embeddedComponent,
    trendingContentPromise,
    isLoading,
    onNavigate,
}: MegaMenuDropdownPanelWithCategoriesProps) {
    if (isLoading || !trendingContentPromise) {
        return <MegaMenuDropdownSkeleton columns={TRENDING_SKELETON_COLUMNS} />;
    }

    return (
        <Suspense fallback={<MegaMenuDropdownSkeleton columns={TRENDING_SKELETON_COLUMNS} />}>
            <Await
                resolve={trendingContentPromise}
                errorElement={<MegaMenuDropdownSkeleton columns={TRENDING_SKELETON_COLUMNS} />}>
                {(trendingContent) => (
                    <ResolvedTrendingPanel
                        trendingContent={trendingContent}
                        embeddedComponent={embeddedComponent}
                        onNavigate={onNavigate}
                    />
                )}
            </Await>
        </Suspense>
    );
}

function ResolvedTrendingPanel({
    trendingContent,
    embeddedComponent,
    onNavigate,
}: MegaMenuDropdownPanelProps & { trendingContent: MegaMenuTrendingContent }) {
    const resolveEmbedded = (resolved: ComponentWithComponentData | null) => {
        const columns: MegaMenuDropdownColumn[] = [];

        if (trendingContent.categories.length > 0) {
            columns.push({
                key: 'trending-now',
                content: (
                    <MegaMenuCategoryLinkColumn
                        title={TRENDING_NOW_COLUMN_TITLE}
                        categories={trendingContent.categories}
                        onNavigate={onNavigate}
                    />
                ),
            });
        }

        if (trendingContent.contentLinks.length > 0) {
            columns.push({
                key: 'inside-space-content',
                content: (
                    <MegaMenuLinkColumn
                        title={INSIDE_SPACE_COLUMN_TITLE}
                        items={trendingContent.contentLinks}
                        onNavigate={onNavigate}
                    />
                ),
            });
        }

        columns.push(
            ...buildEmbeddedColumns(resolved, [
                TRENDING_MEGAMENU_COLUMN_3_REGION_ID,
                TRENDING_MEGAMENU_COLUMN_4_REGION_ID,
            ])
        );

        if (columns.length === 0) {
            return null;
        }

        return <MegaMenuDropdownLayout columns={columns} />;
    };

    if (embeddedComponent instanceof Promise) {
        return (
            <Suspense fallback={<MegaMenuDropdownSkeleton columns={TRENDING_SKELETON_COLUMNS} />}>
                <Await resolve={embeddedComponent} errorElement={resolveEmbedded(null)}>
                    {resolveEmbedded}
                </Await>
            </Suspense>
        );
    }

    return resolveEmbedded(embeddedComponent ?? null);
}

export function CustomMegaMenuPanel({
    embeddedComponent,
    isLoading,
    slot,
}: MegaMenuDropdownPanelProps & { slot: MegaMenuCustomNavSlot }) {
    return (
        <ResolvedEmbeddedOnlyPanel
            embeddedComponent={embeddedComponent}
            isLoading={isLoading}
            regionIds={[...CUSTOM_MEGA_MENU_COLUMN_REGION_IDS[slot]]}
            skeletonColumns={FOUR_FEATURE_SKELETON_COLUMNS}
        />
    );
}
